import { FC, useState } from 'react';
import { Fade, Grid, Box, Divider, Typography, Button, alpha, useMediaQuery } from '@mui/material';
import { Link } from 'react-router-dom';
import { Modal } from '../../../../components';
import { useSnackbar } from 'notistack';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import { ICalendarClosingLocation, IGetCalendarEvent } from '../../../../models/calendar';
import { theme } from '../../../../styles/theme';
import {
  formatShortFriendlyDateWithTime,
  formatShortFriendlyTime,
  formatDate
} from '../../../../helpers';
import { LoadingButton } from '@mui/lab';
import { Download } from '@mui/icons-material';

type IClosingCalendarModal = {
  isOpen: boolean;
  onClose: () => void;
  startDateTime: Date;
  setStartDateTime: (date: Date) => void;
  endDateTime: Date;
  setEndDateTime: (date: Date) => void;
  selectedEvent: IGetCalendarEvent;
  setCalendarDetail: (calendarDetail: IGetCalendarEvent) => void;
  fetchCalendarEvents: () => void;
  downloadEvent: (event) => void;
};

const AddCustomPageSchema = Yup.object().shape({
  title: Yup.string().required('Required'),
  startDateTime: Yup.date().required('Required'),
  endDateTime: Yup.date().required('Required'),
  clientAttendees: Yup.array(),
  internalClosingUsers: Yup.array(Yup.string()),
  location: Yup.string()
});

export const ClosingCalendarModal: FC<IClosingCalendarModal> = ({
  isOpen,
  onClose,
  startDateTime,
  setStartDateTime,
  endDateTime,
  setEndDateTime,
  selectedEvent,
  setCalendarDetail,
  fetchCalendarEvents,
  downloadEvent
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const [isICSDownloading, setIsICSDownloading] = useState(false);
  const isMobile = useMediaQuery('(max-width:600px)');

  // Add space before all Capital letters, except for the beginning and ending of the string
  const formatTitle = title => title?.replace(/([A-Z])/g, ' $1')?.trim();

  const handleClose = () => {
    setCalendarDetail(null);
    onClose();
  };

  return (
    <Formik
      enableReinitialize={true}
      initialValues={{
        isAllDay: false,
        description: '',
        title: selectedEvent ? selectedEvent.title : ''
      }}
      validationSchema={AddCustomPageSchema}
      onSubmit={async (values, actions) => {
        try {
          enqueueSnackbar(
            selectedEvent
              ? 'Successfully updated calendar event!'
              : 'Successfully created calendar event!',
            {
              variant: 'success'
            }
          );

          handleClose();
          fetchCalendarEvents();
          actions.resetForm();
        } catch (error: any) {
          enqueueSnackbar(error?.Detail ?? 'Error saving calendar event, please try again.', {
            variant: 'error'
          });
        }
      }}
    >
      {({ handleSubmit }) => {
        return (
          <Modal
            open={isOpen}
            onClose={() => {
              handleClose();
            }}
            maxWidth="sm"
          >
            <Fade in={isOpen}>
              <Form onSubmit={handleSubmit} autoComplete="none">
                <Box
                  marginBottom="0.5rem"
                  marginTop={theme => theme.spacing(1)}
                  display={'flex'}
                  justifyContent={isMobile ? 'center' : 'space-between'}
                  flexDirection={isMobile ? 'column' : 'row'} // Stack on mobile
                  alignItems={isMobile ? 'center' : 'flex-start'}
                >
                  <Typography variant="h5" marginBottom={isMobile ? 0.75 : 0}>
                    {formatTitle(selectedEvent?.eventType ?? '')}
                  </Typography>
                  <LoadingButton
                    id={'download_ics'}
                    color="primary"
                    variant="contained"
                    onClick={async () => {
                      setIsICSDownloading(true);
                      downloadEvent(selectedEvent);
                      setIsICSDownloading(false);
                    }}
                    disabled={isICSDownloading}
                    loading={isICSDownloading}
                    startIcon={<Download />}
                  >
                    Add to Calendar
                  </LoadingButton>
                </Box>
                <Box marginBottom="0.5rem">
                  <Divider />
                </Box>
                <Grid container spacing={1}>
                  <Grid item xs={12} sm={6}>
                    {selectedEvent?.sageId > 0 && informationItem('Sage ID', selectedEvent?.sageId)}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {selectedEvent?.title &&
                      informationItem(
                        'Purchaser',
                        <Link
                          to={`/sales/contracting/under-contract/${selectedEvent?.buildId}/contract-dates`}
                          style={{
                            color: alpha(theme.palette.common.black, 0.87)
                          }}
                        >
                          {selectedEvent?.title}
                        </Link>
                      )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {selectedEvent?.title &&
                      informationItem(
                        selectedEvent?.eventType === 'Homeowner Celebration' ||
                          selectedEvent?.eventType === 'Homeowner Orientation'
                          ? 'Location'
                          : 'Address',
                        selectedEvent?.address
                      )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {selectedEvent?.title &&
                      informationItem(
                        'Neighborhood',
                        formatNeighborhood(
                          selectedEvent?.neighborhoodName,
                          selectedEvent?.phase,
                          selectedEvent?.lot
                        )
                      )}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {selectedEvent?.eventType === 'Closing Appointment' &&
                      !selectedEvent?.location &&
                      Object?.values?.(selectedEvent?.closingLocation ?? {})?.some(
                        value => !!value
                      ) &&
                      informationItem(
                        'Location',
                        formatClosingLocation(selectedEvent?.closingLocation)
                      )}
                    {selectedEvent?.location &&
                      informationItem('Location', selectedEvent?.location)}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    {selectedEvent?.start &&
                      !selectedEvent?.isAllDay &&
                      informationItem(
                        'Date',
                        `${formatShortFriendlyDateWithTime(
                          new Date(selectedEvent?.start)
                        )} - ${formatShortFriendlyTime(new Date(selectedEvent?.end))}`
                      )}
                    {selectedEvent?.start &&
                      selectedEvent?.isAllDay &&
                      informationItem(
                        'Date',
                        `${formatDate(new Date(selectedEvent?.start))} - Remote Signing`
                      )}
                  </Grid>
                  <Grid item xs={12}>
                    {selectedEvent?.eventType === 'Closing Appointment' &&
                      selectedEvent?.closingNotes &&
                      informationItem('Closing Notes', selectedEvent?.closingNotes)}
                  </Grid>
                </Grid>
                <Button
                  variant="contained"
                  onClick={() => {
                    handleClose();
                  }}
                  fullWidth
                >
                  Close
                </Button>
              </Form>
            </Fade>
          </Modal>
        );
      }}
    </Formik>
  );
};
const formatClosingLocation = (location: ICalendarClosingLocation) => {
  return (
    <Box marginBottom="0.5rem">
      <Typography>{location?.line1}</Typography>
      {location?.line2 && <Typography>{location?.line2}</Typography>}
      {(location?.city || location?.state || location?.zip) && (
        <Typography>
          {location?.city ? location.city : ''}
          {location?.state ? `${location.city ? ', ' : ''}${location?.state}` : ''}
          {location?.zip ? ` ${location?.zip}` : ''}
        </Typography>
      )}
    </Box>
  );
};
const formatNeighborhood = (neighborhood: string, phase: string, lot: string | number) => {
  return `${neighborhood}${phase ? ` Phase ${phase}` : ''}${lot ? `, Lot ${lot}` : ''}`;
};
export const informationItem = (label: string, value: string | number | JSX.Element) => (
  <Box marginBottom="0.5rem">
    <Typography fontWeight={'bold'}>{label}: </Typography>
    <Typography>{value}</Typography>
  </Box>
);
